import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useGameStore } from "../store/game";
import { useSettingsStore } from "../store/settings";
import { GuessInput } from "../components/GuessInput";
import { GuessFeed } from "../components/GuessFeed";
import { ResultModal } from "../components/ResultModal";
import { TimerBar } from "../components/TimerBar";
import { HeatBar } from "../components/HeatBar";
import { formatSim, heatColor, heatLabel } from "../lib/heat";

export function Play() {
  const loadSettings = useSettingsStore((s) => s.load);
  const loaded = useSettingsStore((s) => s.loaded);
  const round = useGameStore((s) => s.round);
  const guesses = useGameStore((s) => s.guesses);
  const status = useGameStore((s) => s.status);
  const error = useGameStore((s) => s.error);
  const lastGuessId = useGameStore((s) => s.lastGuessId);
  const revealedTarget = useGameStore((s) => s.revealedTarget);
  const start = useGameStore((s) => s.start);
  const guess = useGameStore((s) => s.guess);
  const giveUp = useGameStore((s) => s.giveUp);
  const timeOut = useGameStore((s) => s.timeOut);
  const reset = useGameStore((s) => s.reset);

  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    if (!loaded) loadSettings();
  }, [loaded, loadSettings]);

  // auto-start if landing on /play with no active round
  useEffect(() => {
    if (status === "idle") void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === "won" || status === "lost" || status === "revealed") {
      setShowResult(true);
    }
  }, [status]);

  const bestSim = useMemo(
    () => guesses.reduce((m, g) => Math.max(m, g.similarity), 0),
    [guesses],
  );
  const latestGuess = useMemo(
    () => guesses.find((g) => g.id === lastGuessId) ?? null,
    [guesses, lastGuessId],
  );

  if (status === "resuming" || status === "starting" || (status === "idle" && !error)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <motion.div
          className="w-10 h-10 rounded-full border-2 border-accent/30 border-t-accent"
          animate={{ rotate: 360 }}
          transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
        />
        <p className="text-ink-400 text-sm">
          {status === "resuming" ? "resuming round…" : "dealing target…"}
        </p>
      </div>
    );
  }

  if (status === "idle" && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-red-400 text-sm max-w-md text-center">{error}</p>
        <button
          onClick={() => void start()}
          className="px-5 py-2 rounded-xl bg-accent text-ink-950 font-medium hover:opacity-90"
        >
          try again
        </button>
      </div>
    );
  }

  const inProgress = status === "playing";

  return (
    <div className="flex flex-col gap-5">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-5 flex flex-col gap-3"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm text-ink-300">
            <span className="px-2 py-0.5 rounded-md bg-white/5 font-mono text-xs uppercase">
              {round?.targetType}
            </span>
            <span className="px-2 py-0.5 rounded-md bg-white/5 font-mono text-xs uppercase">
              {round?.language}
            </span>
            <span className="px-2 py-0.5 rounded-md bg-white/5 font-mono text-xs uppercase">
              {round?.mode}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => giveUp()}
              disabled={!inProgress}
              className="px-3 py-1.5 rounded-md text-ink-300 hover:text-ink-100 hover:bg-white/5 disabled:opacity-40"
            >
              give up
            </button>
            <button
              onClick={() => {
                reset();
                setShowResult(false);
                void start();
              }}
              className="px-3 py-1.5 rounded-md text-ink-300 hover:text-ink-100 hover:bg-white/5"
            >
              new round
            </button>
          </div>
        </div>

        {round?.mode === "timeattack" && round.timeLimitSeconds && inProgress && (
          <TimerBar
            startedAt={round.startedAt}
            durationSec={round.timeLimitSeconds}
            onExpire={() => timeOut()}
          />
        )}

        <div className="flex items-center justify-between gap-4">
          <div className="text-xs text-ink-400">
            <span className="block">best so far</span>
            <span
              className="text-2xl font-mono tabular-nums"
              style={{ color: heatColor(bestSim) }}
            >
              {formatSim(bestSim)}
            </span>
            <span
              className="ml-2 text-xs uppercase tracking-wider"
              style={{ color: heatColor(bestSim) }}
            >
              {heatLabel(bestSim)}
            </span>
          </div>
          <div className="text-right text-xs text-ink-400">
            <span className="block">guesses</span>
            <span className="text-2xl font-mono tabular-nums text-ink-100">{guesses.length}</span>
          </div>
        </div>
        <HeatBar value={bestSim} height={8} />
      </motion.div>

      {inProgress && (
        <GuessInput
          onSubmit={guess}
          placeholder={
            round?.targetType === "sentence" ? "type a guess sentence…" : "type a word…"
          }
        />
      )}

      {latestGuess && (
        <motion.div
          key={latestGuess.id}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center text-sm text-ink-400"
        >
          last guess: <span className="font-mono text-ink-100">{latestGuess.text}</span> →{" "}
          <span style={{ color: heatColor(latestGuess.similarity) }}>
            {formatSim(latestGuess.similarity)} ({heatLabel(latestGuess.similarity)})
          </span>
        </motion.div>
      )}

      <GuessFeed guesses={guesses} lastGuessId={lastGuessId} />

      <ResultModal
        open={showResult}
        outcome={status === "won" ? "won" : status === "lost" ? "lost" : "revealed"}
        target={revealedTarget}
        sourceWord={round?.sourceWord ?? null}
        bestSimilarity={bestSim}
        guessCount={guesses.length}
        onPlayAgain={() => {
          setShowResult(false);
          reset();
          void start();
        }}
        onClose={() => setShowResult(false)}
      />

      {error && <p className="text-red-400 text-sm text-center">{error}</p>}
    </div>
  );
}
